import { $generateNodesFromDOM } from "@lexical/html";
import { LexicalEditor } from "lexical";
import { marked } from "marked";

/**
 * Converts markdown to HTML using marked, then generates Lexical nodes
 * using @lexical/html's $generateNodesFromDOM
 * Also handles code blocks specially to create proper CodeNode instances
 */
export function convertMarkdownToLexicalNodes(editor: LexicalEditor, markdown: string) {
  try {
    // Helper function to escape HTML
    const escapeHtml = (text: string) => {
      const map: Record<string, string> = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      };
      return text.replace(/[&<>"']/g, (char) => map[char]);
    };

    // Split markdown into lines to process checklists and code blocks separately
    const lines = markdown.split("\n");
    let html = "";
    let inCheckList = false;
    let checkListItems: string[] = [];
    let regularMarkdown = "";
    let inCodeBlock = false;
    let codeBlockContent = "";
    let codeBlockLanguage = "";
    let inBlockEquation = false;
    let blockEquationContent = "";

    for (const line of lines) {
      // Trim the line to remove \r and other whitespace at the end
      const trimmedLine = line.trim();

      // Check for block equation markers ($$)
      if (trimmedLine.startsWith("$$")) {
        if (inBlockEquation) {
          // End of block equation
          inBlockEquation = false;

          // Close any open checklist
          if (inCheckList && checkListItems.length > 0) {
            html += `<ul class="task-list" __lexicallisttype="check">${checkListItems.join("")}</ul>`;
            checkListItems = [];
            inCheckList = false;
          }

          // Convert block equation to HTML format that EquationNode can import
          const equation = btoa(blockEquationContent.trim());
          html += `<div data-lexical-equation="${equation}" data-lexical-inline="false" class="e_eqn">${blockEquationContent.trim()}</div>`;
          blockEquationContent = "";
          regularMarkdown = "";
          continue;
        } else {
          // Start of block equation
          // If we were processing regular markdown, convert it first
          if (regularMarkdown.trim()) {
            html += marked(regularMarkdown);
            regularMarkdown = "";
          }

          // Close any open checklist
          if (inCheckList && checkListItems.length > 0) {
            html += `<ul class="task-list" __lexicallisttype="check">${checkListItems.join("")}</ul>`;
            checkListItems = [];
            inCheckList = false;
          }

          inBlockEquation = true;
          continue;
        }
      }

      // If we're in a block equation, accumulate content
      if (inBlockEquation) {
        blockEquationContent += line + "\n";
        continue;
      }

      // Check for code block markers
      if (trimmedLine.startsWith("```")) {
        if (inCodeBlock) {
          // End of code block
          inCodeBlock = false;

          // Close any open checklist
          if (inCheckList && checkListItems.length > 0) {
            html += `<ul class="task-list" __lexicallisttype="check">${checkListItems.join("")}</ul>`;
            checkListItems = [];
            inCheckList = false;
          }

          // Store code block as a special marker that we'll handle in post-processing
          html += `<pre class="code-block" data-language="${escapeHtml(codeBlockLanguage)}"><code>${escapeHtml(codeBlockContent.trimEnd())}</code></pre>`;
          codeBlockContent = "";
          codeBlockLanguage = "";
          regularMarkdown = "";
          continue;
        } else {
          // Start of code block
          // If we were processing regular markdown, convert it first
          if (regularMarkdown.trim()) {
            html += marked(regularMarkdown);
            regularMarkdown = "";
          }

          // Close any open checklist
          if (inCheckList && checkListItems.length > 0) {
            html += `<ul class="task-list" __lexicallisttype="check">${checkListItems.join("")}</ul>`;
            checkListItems = [];
            inCheckList = false;
          }

          inCodeBlock = true;
          // Extract language from opening marker (e.g., ```javascript -> javascript)
          codeBlockLanguage = trimmedLine.slice(3).trim();
          continue;
        }
      }

      // If we're in a code block, accumulate content
      if (inCodeBlock) {
        codeBlockContent += line + "\n";
        continue;
      }

      // Match checklist items: "- [x]", "- [ ]", with optional leading whitespace
      const checklistMatch = trimmedLine.match(/^(\s*)- \[([^\]]*)\]\s+(.*)$/);

      if (checklistMatch) {
        // If we were processing regular markdown, convert it first
        if (regularMarkdown.trim()) {
          html += marked(regularMarkdown);
          regularMarkdown = "";
        }

        inCheckList = true;
        const isChecked = checklistMatch[2].toLowerCase() === "x";
        const text = escapeHtml(checklistMatch[3]);
        const liHtml = `<li role="checkbox" aria-checked="${isChecked ? "true" : "false"}" value="1"><span style="white-space: pre-wrap;">${text}</span></li>`;
        checkListItems.push(liHtml);
      } else {
        // If we were in a checklist, close it
        if (inCheckList && checkListItems.length > 0) {
          html += `<ul class="task-list" __lexicallisttype="check">${checkListItems.join("")}</ul>`;
          checkListItems = [];
          inCheckList = false;
        }

        // Only add non-empty lines to regular markdown or skip empty lines when transitioning
        if (trimmedLine || !inCheckList) {
          regularMarkdown += line + "\n";
        }
      }
    }

    // Handle unclosed block equation
    if (inBlockEquation && blockEquationContent) {
      // Close any open checklist
      if (inCheckList && checkListItems.length > 0) {
        html += `<ul class="task-list" __lexicallisttype="check">${checkListItems.join("")}</ul>`;
        checkListItems = [];
        inCheckList = false;
      }

      const equation = btoa(blockEquationContent.trim());
      html += `<div data-lexical-equation="${equation}" data-lexical-inline="false" class="e_eqn">${blockEquationContent.trim()}</div>`;
    }

    // Handle unclosed code block
    if (inCodeBlock && codeBlockContent) {
      // Close any open checklist
      if (inCheckList && checkListItems.length > 0) {
        html += `<ul class="task-list" __lexicallisttype="check">${checkListItems.join("")}</ul>`;
        checkListItems = [];
        inCheckList = false;
      }

      html += `<pre class="code-block" data-language="${escapeHtml(codeBlockLanguage)}"><code>${escapeHtml(codeBlockContent.trimEnd())}</code></pre>`;
    }

    // Close any remaining checklist
    if (inCheckList && checkListItems.length > 0) {
      html += `<ul class="task-list" __lexicallisttype="check">${checkListItems.join("")}</ul>`;
    }

    // Convert any remaining regular markdown
    if (regularMarkdown.trim()) {
      html += marked(regularMarkdown);
    }

    // Convert inline equations ($...$) in the HTML
    // This regex finds $ ... $ patterns that are not part of block equations
    const inlineEquationRegex = /(?<!\$)\$(?!\$)([^$]+?)\$(?!\$)/g;
    html = html.replace(inlineEquationRegex, (match, equation) => {
      const trimmedEq = equation.trim();
      if (trimmedEq) {
        const encoded = btoa(trimmedEq);
        return `<span data-lexical-equation="${encoded}" data-lexical-inline="true" class="e_eqn">${trimmedEq}</span>`;
      }
      return match;
    });

    // Create a temporary DOM element to hold the HTML
    const parser = new DOMParser();
    const dom = parser.parseFromString(`${html}`, "text/html");

    // Generate Lexical nodes from the DOM using the editor instance
    // The @lexical/html conversion will handle <pre><code> as CodeNode
    const nodes = $generateNodesFromDOM(editor, dom);

    return nodes;
  } catch (error) {
    console.error("Error converting markdown to Lexical nodes:", error);
    return [];
  }
}
